import { useEffect, useRef, useState } from "react";

export const canListen = () => typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

const COMMANDS = [
  [/\b(next|forward|done|go on|next step)\b/, "next"],
  [/\b(back|previous|go back|last step)\b/, "back"],
  [/\b(repeat|again|say that again|what was that|pardon)\b/, "repeat"],
  [/\b(start|set|begin)\b.*\btimer\b|\btimer\b.*\b(start|go)\b/, "timer_start"],
  [/\b(pause|stop|hold)\b.*\btimer\b|\btimer\b.*\b(pause|stop)\b/, "timer_pause"],
  [/\b(quiet|hush|shush|mute|be quiet)\b/, "hush"],
];

export function parseCommand(transcript) {
  const t = ` ${transcript.toLowerCase().trim()} `;
  for (const [re, cmd] of COMMANDS) if (re.test(t)) return cmd;
  return null;
}

// Continuous, hands-free command listening; pauses while Millie talks so she doesn't hear herself.
export default function useVoiceCommands({ enabled, paused, onCommand }) {
  const [listening, setListening] = useState(false);
  const [heard, setHeard] = useState("");
  const recRef = useRef(null);
  const cbRef = useRef(onCommand);
  cbRef.current = onCommand;
  const lastRef = useRef(0);

  useEffect(() => {
    if (!enabled || paused || !canListen()) { recRef.current?.stop(); recRef.current = null; setListening(false); return undefined; }
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Rec();
    rec.lang = "en-AU"; rec.continuous = true; rec.interimResults = false;
    let alive = true;
    rec.onresult = (e) => {
      const res = e.results[e.results.length - 1];
      if (!res.isFinal) return;
      const text = res[0].transcript;
      setHeard(text);
      const cmd = parseCommand(text);
      if (cmd && Date.now() - lastRef.current > 1200) { lastRef.current = Date.now(); cbRef.current?.(cmd, text); }
    };
    rec.onstart = () => setListening(true);
    rec.onerror = (e) => { if (e.error === "not-allowed" || e.error === "service-not-allowed") { alive = false; cbRef.current?.("denied"); } };
    rec.onend = () => { setListening(false); if (alive) setTimeout(() => { try { rec.start(); } catch { /* already started */ } }, 300); };
    recRef.current = rec;
    try { rec.start(); } catch { /* ignore */ }
    return () => { alive = false; rec.onend = null; try { rec.stop(); } catch { /* ignore */ } recRef.current = null; setListening(false); };
  }, [enabled, paused]);

  return { listening, heard, supported: canListen() };
}
